/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  belongsTo,
  attr,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import type School from 'linced-app/models/school';

// Proto: finance.Ledger.
export default class Ledger extends Model {
  declare [Type]: 'finance/ledger';

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @attr('string', { defaultValue: '' })
  declare campusId: string; // TODO: implement when we have Campuses

  @attr('number')
  declare updateTime: number | undefined;

  @attr('number')
  declare createTime: number | undefined;

  @attr('string', { defaultValue: '' })
  declare code: string;

  @attr('string', { defaultValue: '' })
  declare name: string;

  @computed('code', 'name')
  get displayName() {
    if (isEmpty(this.name)) {
      return this.code;
    }
    return `${this.code} - ${this.name}`;
  }

  @attr('number', { defaultValue: 0 })
  declare taxType: number;

  @attr('boolean', { defaultValue: false })
  declare isHidden: boolean;

  // Extra properties for NSW.
  // glAccount is the General Ledger Account code for this Ledger.
  @attr('string', { defaultValue: '' })
  declare glAccount: string;

  // glAccountDescription is a human-readable description of the GL Account.
  @attr('string', { defaultValue: '' })
  declare glAccountDescription: string;

  // fundCode is the Fund Code for this Ledger.
  @attr('string', { defaultValue: '' })
  declare fundCode: string;

  // fundCodeDescription is a human-readable description of the Fund Code.
  @attr('string', { defaultValue: '' })
  declare fundCodeDescription: string;

  // isEnforceable flags whether this Ledger covers enforceable debts.
  @attr('boolean', { defaultValue: false })
  declare isEnforceable: boolean;

  // isDeductibleGiftReceipt flags whether this Ledger is for DGRs.
  @attr('boolean', { defaultValue: false })
  declare isDeductibleGiftReceipt: boolean;

  // effectiveFrom is the date from which this Ledger is effective.
  @attr('string')
  declare effectiveFrom: string | undefined;

  // effectiveTo is the date until which this Ledger is effective.
  @attr('string')
  declare effectiveTo: string | undefined;

  // isActive flags whether the GL Account and Fund Code are both active.
  @attr('boolean', { defaultValue: false })
  declare isActive: boolean;
}
